import { OpenGeometry } from 'opengeometry';
import { Glyphs } from '@opengeometry/openglyph';
import * as THREE from 'three';
import { OpenThree } from './packages/openplans-three/src/three';
import { CameraMode, PlanCamera } from './packages/openplans-three/src/plancamera';

import { RectanglePrimitive, RectangleOptions, ArcPrimitive, ArcOptions, PolylinePrimitive, PolylineOptions, LineOptions, LinePrimitive  } from './packages/openplans-core/src/primitives';
import { CuboidOptions, CuboidShape, CylinderOptions, CylinderShape } from './packages/openplans-core/src/shapes';
import {
  Door,
  OpenElement,
  SingleWall,
  PolyWall,
  WallOpening,
  Window,
  Slab,
  Floor,
} from './packages/openplans-core/src/elements';
import type { OpenElementLoadOverrides } from './packages/openplans-core/src/elements';
import type { OPBaseInput, OPBaseLoadOverrides } from './packages/openplans-core/src/projects';
import { loadOPBaseAssetSources, OPBaseProject } from './packages/openplans-core/src/projects';

import { ViewManager } from './packages/openplans-core/src/view-manager';
import { DimensionTool, DimensionType } from './packages/openplans-core/src/dimensions';
import { ViewportBlock, ViewportConfig } from './packages/openplans-core/src/layouts/viewport-block';

import {
  Level,
  Grid,
  ReferencePlane,
  ProjectOrigin,
  SectionLine,
  ElevationMarker,
} from './packages/openplans-core/src/datums';

import { Event } from './utils/event';
import { Opening } from './packages/openplans-core/src/elements/openings/opening';

// Camera Modes
export { CameraMode } from './packages/openplans-three/src/plancamera';
export * from './packages/openplans-core/src';
export { Door, OpenElement, OPBaseProject, Window, SingleWall, PolyWall, WallOpening, Slab, Floor, Opening };

export type Theme = 'light' | 'dark' | 'darkBlue';

export class OpenPlans {
  private openThree: OpenThree;
  private planCamera: PlanCamera;

  private elements: Array<THREE.Object3D> = [];

  // TODO: Make this optional later and lazy load when needed
  viewManager: ViewManager = new ViewManager();

  // Events
  onRender: Event<any> = new Event<any>();

  set CameraMode(mode: CameraMode) {
    this.planCamera.CameraMode = mode;
  }

  get CameraMode() {
    return this.planCamera.CameraMode;
  }

  constructor(container: HTMLElement) {
    this.openThree = new OpenThree(container, this.renderCallback);
    this.planCamera = this.openThree.planCamera;
  }

  async setupOpenGeometry(wasmURL?: string) {
    await OpenGeometry.create({
      wasmURL: wasmURL
    });

    DimensionTool.sceneRef = this.openThree.scene;

    Glyphs.scene = this.openThree.scene;
    Glyphs.camera = this.openThree.threeCamera;
    await Glyphs.loadFaces('Source_Code_Pro_Regular');
  }

  private renderCallback = () => {
    this.onRender.trigger();
    this.getElementsByType(ViewportBlock).forEach((vb) => {
      vb.render(this.openThree.renderer, this.openThree.scene);
    });
  }

  fitToView(elements: THREE.Object3D[]) {
    const controls = this.planCamera.controls;
    const boundingBox = new THREE.Box3();
    elements.forEach((element) => {
      boundingBox.expandByObject(element);
    });
    controls.fitToSphere(boundingBox.getBoundingSphere(new THREE.Sphere()), true);
  }

  toggleGrid(show: boolean) {
    this.openThree.toggleGrid(show);
  }

  private addElement<T extends THREE.Object3D>(element: T) {
    this.openThree.scene.add(element);
    this.elements.push(element);
    return element;
  }

  getElementById(id: string) {
    return this.elements.find(el => el.userData.ogid === id);
  }

  getElementsByType<T extends THREE.Object3D>(type: new (...args: any[]) => T): T[] {
    return this.elements.filter(el => el instanceof type) as T[];
  }

  arc(config?: ArcOptions) {
    const arc = new ArcPrimitive(config);
    return this.addElement(arc);
  }

  rectangle(config?: RectangleOptions) {
    const rectangle = new RectanglePrimitive(config);
    return this.addElement(rectangle);
  }

  polyline(config?: PolylineOptions) {
    const polyline = new PolylinePrimitive(config);
    return this.addElement(polyline);
  }

  line(config?: LineOptions) {
    const line = new LinePrimitive(config);
    return this.addElement(line);
  }

  // Shapes
  cuboid(config?: CuboidOptions) {
    const cuboid = new CuboidShape(config);
    return this.addElement(cuboid);
  }

  cylinder(config?: CylinderOptions) {
    const cylinder = new CylinderShape(config);
    return this.addElement(cylinder);
  }

  // Elements
  opening(config?: ConstructorParameters<typeof Opening>[0]) {
    return this.addElement(new Opening(config));
  }

  singleWall(config?: ConstructorParameters<typeof SingleWall>[0]) {
    const singleWall = new SingleWall(config);
    return this.addElement(singleWall);
  }

  polyWall(config?: ConstructorParameters<typeof PolyWall>[0]) {
    return this.addElement(new PolyWall(config));
  }

  wallOpening(config?: ConstructorParameters<typeof WallOpening>[0]) {
    return this.addElement(new WallOpening(config));
  }

  door(config?: ConstructorParameters<typeof Door>[0]) {
    return this.addElement(new Door(config));
  }

  window(config?: ConstructorParameters<typeof Window>[0]) {
    return this.addElement(new Window(config));
  }

  slab(config?: ConstructorParameters<typeof Slab>[0]) {
    return this.addElement(new Slab(config));
  }

  genericElement(config?: ConstructorParameters<typeof OpenElement>[0]) {
    return this.addElement(new OpenElement(config));
  }

  async loadGenericElement(
    url: string,
    overrides?: OpenElementLoadOverrides,
  ) {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load generic element from "${url}": ${response.status} ${response.statusText}`);
    }

    const source = await response.text();
    return this.genericElement({
      ...(overrides ?? {}),
      source,
    });
  }

  opBase(config?: OPBaseInput) {
    return this.addElement(new OPBaseProject(config));
  }

  async loadOPBase(
    url: string,
    overrides?: OPBaseLoadOverrides,
  ) {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load project from "${url}": ${response.status} ${response.statusText}`);
    }

    const source = await response.text();
    const document = OPBaseProject.parseBuffer(source);
    const assetSources = await loadOPBaseAssetSources(document, url);

    return this.opBase({
      ...(overrides ?? {}),
      source: document,
      assetBaseUrl: url,
      assetSources: {
        ...assetSources,
        ...(overrides?.assetSources ?? {}),
      },
    });
  }

  resolveProject(project?: OPBaseProject | string) {
    const target = this.resolveProjectTarget(project);
    return target.resolveProject();
  }

  generateExportDocument(project?: OPBaseProject | string) {
    return this.resolveProject(project);
  }

  private resolveProjectTarget(project?: OPBaseProject | string) {
    if (project instanceof OPBaseProject) {
      return project;
    }

    if (typeof project === 'string') {
      const match = this.getElementsByType(OPBaseProject).find((entry) => entry.ogid === project || entry.name === project);
      if (!match) {
        throw new Error(`Unable to resolve project "${project}".`);
      }
      return match;
    }

    const projects = this.getElementsByType(OPBaseProject);
    const fallback = projects.length > 0 ? projects[projects.length - 1] : undefined;
    if (!fallback) {
      throw new Error('No OPBaseProject is currently loaded.');
    }

    return fallback;
  }

  floor(config?: ConstructorParameters<typeof Floor>[0]) {
    return this.addElement(new Floor(config));
  }

  // Datums — reference geometry (levels, grids, reference planes, origin,
  // section lines, elevation markers).
  level(config?: ConstructorParameters<typeof Level>[0]) {
    return this.addElement(new Level(config));
  }

  grid(config?: ConstructorParameters<typeof Grid>[0]) {
    return this.addElement(new Grid(config));
  }

  referencePlane(config?: ConstructorParameters<typeof ReferencePlane>[0]) {
    return this.addElement(new ReferencePlane(config));
  }

  projectOrigin(config?: ConstructorParameters<typeof ProjectOrigin>[0]) {
    return this.addElement(new ProjectOrigin(config));
  }

  sectionLine(config?: ConstructorParameters<typeof SectionLine>[0]) {
    return this.addElement(new SectionLine(config));
  }

  elevationMarker(config?: ConstructorParameters<typeof ElevationMarker>[0]) {
    return this.addElement(new ElevationMarker(config));
  }

  // Dimensions
  createDimension(type: DimensionType) {
    return DimensionTool.createDimension(type);
  }

  // Layouts
  viewportBlock(config: Omit<ViewportConfig, 'ogType' | 'scale' | 'rotation'> & Partial<Pick<ViewportConfig, 'scale' | 'rotation'>>) {
    const fullConfig: ViewportConfig = { ogType: 'VIEWPORT_BLOCK', scale: 1, rotation: 0, ...config };
    return this.addElement(new ViewportBlock(fullConfig));
  }
}
